import { ListButton } from "@refinedev/antd";
import {
  Select,
  Spin,
  Switch,
  Typography,
  message,
  Card,
  Collapse,
  Modal,
  InputNumber,
} from "antd";
import { useNavigation, useTranslate } from "@refinedev/core";
import {
  AppstoreOutlined,
  CheckOutlined,
  MailOutlined,
  WhatsAppOutlined,
  CopyOutlined,
  ContactsOutlined,
  DeleteOutlined,
  ReadOutlined,
  TeamOutlined,
  ArrowLeftOutlined,
  ShopOutlined,
  ApiOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { Flex, Button, theme } from "antd";
import { useContext, useEffect, useState } from "react";
import { SmartMenuContext } from "@/config/context/smartMenuContext";
import { axiosInstance, marginUnitLabel } from "@/utilities";
import {
  API_URL_PERMISSIONS,
  API_URL_PERMISSIONS_STORES,
  API_URL_STORE_LINKED_SELLER,
} from "@/services/XCONS_ENV";
import { useLocation } from "react-router-dom";
import { BoxCloseIcon, KeyRound, PresupuestoIcon } from "@/components";
import { NIVEL } from "@/utilities/niveles-de-permiso";

const PERMISSION_OPTIONS = [
  { label: NIVEL.admin, value: "admin" },
  { label: NIVEL.editor, value: "editor" },
  { label: NIVEL.viewer, value: "viewer" },
  { label: NIVEL.no_access, value: "no_access" },
];

type PermissionItem = {
  code: string;
  value: string;
  description?: string;
};

type ConfigItem = {
  code: string;
  value: string;
  description?: string;
};

const { Option } = Select;
const { Title, Text, Link } = Typography;
const { Panel } = Collapse;

export const LinkedStoreUserPermissionsPanel = ({
  typeIdentifier = "seller",
}: {
  typeIdentifier?: string;
}) => {
  const [structureLoading, setStructureLoading] = useState<boolean>(false);
  const [permissions, setPermissions] = useState<PermissionItem[]>([]);
  const [configs, setConfigs] = useState<ConfigItem[]>([]);
  const [isUserActive, setIsUserActive] = useState<boolean>(true);
  const [isStoreActive, setIsStoreActive] = useState<boolean>(true);
  const [storesPermissions, setStoresPermissions] = useState<any>();

  const t = useTranslate();
  const { token } = theme.useToken();
  const { goBack } = useNavigation();

  const location = useLocation();
  const record = location.state?.record;

  const { smartMenuName } = useContext(SmartMenuContext);

  const storeIdentifier = encodeURIComponent(
    smartMenuName?.replace(/\"/g, "").toLowerCase(),
  );

  const handleBack = () => {
    goBack();
  };

  const getPermissions = async () => {
    setStructureLoading(true);
    try {
      const [res1] = await Promise.all([
        axiosInstance.get(
          `${API_URL_PERMISSIONS}/store_seller/${record?.storeCode}/users/${record?.sellerCode}`,
        ),
      ]);

      const data1 = res1.data;

      console.log("data1", data1);

      const mergedPermissions = [...(data1.permissions || [])];
      const mergedConfigs = [...(data1.configs || [])];

      const dedupedPermissions = Object.values(
        Object.fromEntries(mergedPermissions.map((p) => [p.code, p])),
      );
      const dedupedConfigs = Object.values(
        Object.fromEntries(mergedConfigs.map((c) => [c.code, c])),
      );

      // setIsUserActive(data2.isActive);
      setPermissions(dedupedPermissions as PermissionItem[]);
      setConfigs(dedupedConfigs as ConfigItem[]);
      setIsStoreActive(record?.storeLinkedSellers[0]?.active === 1 ? true : false);
    } catch (error) {
      console.error("Error al obtener permisos:", error);
    } finally {
      setStructureLoading(false);
    }
  };

  const getStores = async () => {
    setStructureLoading(true);
    try {
      const res = await axiosInstance.get(
        `${API_URL_STORE_LINKED_SELLER}/${storeIdentifier}/stores?filter.storeCode=${record?.storeCode}`,
      );

      const data = res.data.data;
      const filtered = data.filter((store: any) => store.isActive === 1);
      setStoresPermissions(filtered);
    } catch (error) {
      console.error("Error al obtener permisos:", error);
    } finally {
      setStructureLoading(false);
    }
  };

  useEffect(() => {
    if (!record) {
      console.error("User ID no encontrado en el registro.");
      handleBack();
      return;
    }
    getPermissions();
    getStores();
  }, []);

  const getPermission = (code: string) =>
    permissions.find((p) => p.code === code);

  const getConfig = (code: string) => configs.find((c) => c.code === code);

  const renderPermissionValue = (item: any, isConfig = false) => {
    if (!item) return null;

    return (
      <Text className="text-permission">{item.value || item.defaultValue}</Text>
    );
  };

  const PERMISSION_GROUPS = [
    {
      key: "contacts",
      icon: <ContactsOutlined />,
      title: t("menu.label.contacts"),
      subtitle: t("label.instruction.permissionscontact"),
      permissionCodes: [],
      configCodes: ["contact_conversation", "contact_quote"],
    },
    {
      key: "contacts_groups",
      icon: <TeamOutlined />,
      title: t("label.contactsgroups"),
      subtitle: t("label.permissionscontact.group"),
      permissionCodes: ["contact_group"],
      configCodes: [],
      noButton: true,
      singleHeader: true,
    },
    {
      key: "quotes",
      icon: <PresupuestoIcon />,
      title: t("menu.label.quotes"),
      subtitle: t("label.instruction.permissionsquotes"),
      permissionCodes: [],
      configCodes: [
        "quote_participation",
        "quote_max_manual_discount",
        "quote_access_margins",
        "quote_commission_discount_level",
      ],
    },
    {
      key: "orders",
      icon: <BoxCloseIcon />,
      title: t("menu.label.orders"),
      mainPermissionCode: "order",
      subtitle: t("label.instruction.orders"),
      permissionCodes: ["order_payment", "order_shipment"],
      configCodes: [],
    },
    {
      // Lo que el store le dio a este seller sobre su árbol de categorías (xcons-docs#445).
      // Acá se ve y no se cambia: lo decide el store, en su pantalla del vínculo.
      key: "categorias",
      icon: <AppstoreOutlined />,
      title: t("label.permissions.categorias"),
      subtitle: t(
        "label.instruction.categorias",
        "Qué pueden hacer tus operadores con el árbol de categorías de este store.",
      ),
      permissionCodes: ["categorias"],
      configCodes: [],
      noButton: true,
      singleHeader: true,
    },
    {
      key: "linked_products",
      icon: <ApiOutlined />,
      title: t("linkedProducts.title"),
      permissionCodes: ["linked_products"],
      configCodes: [],
      individually: true,
    },
    {
      key: "linked_store",
      icon: <ShopOutlined />,
      title: t("contacts.groupsTable.linkedStore"),
      permissionCodes: ["store_linked_seller"],
      configCodes: [],
      individually: true,
    },
    {
      key: "seller_permission",
      icon: <KeyRound />,
      title: t("create.seller.labelSeller"),
      permissionCodes: ["seller_permission"],
      configCodes: [],
      individually: true,
    },
  ];

  const groupedPermissions = PERMISSION_GROUPS.filter((g) => !g.individually);

  const LABELS_MAP: Record<string, React.ReactNode> = {
    contact_conversation: (
      <Text>
        {t("label.permissionscontact.conversation.part1")}
        <strong>{t("label.permissionscontact.conversation.part2")}</strong>
        {t("label.permissionscontact.conversation.part3")}
        <strong>{t("label.permissionscontact.conversation.part4")}</strong>
        {t("label.permissionscontact.conversation.part5")}
      </Text>
    ),
    contact_quote: <Text>{t("label.permissionscontact.quote")}</Text>,
    contact_group: (
      <Flex vertical>
        <Text strong>
          <TeamOutlined /> {t("label.contactsgroups")}
        </Text>
        <Flex>
          <Text style={{ fontSize: "12px", marginLeft: 16 }}>
            {t("label.permissionscontact.group")}
          </Text>
        </Flex>
      </Flex>
    ),
    quote_participation: <Text>{t("label.permissionsquotes")}</Text>,
    quote_max_manual_discount: <Text>{t("label.maximumdiscount")}</Text>,
    quote_access_margins: <Text>{t("label.accessmargins")}</Text>,
    quote_commission_discount_level: (
      <Text>{t("label.commissionlevel")}</Text>
    ),
    order_payment: <Text>{t("label.permissionspaymenttransactions")}</Text>,
    order_shipment: <Text>{t("Label.deliveries")}</Text>,
    store_permission: <Text strong>{t("contacts.tag.storeusers")}</Text>,
    seller_permission: <Text strong>{t("contacts.tag.sellerusers")}</Text>,
    linked_products: <Text strong>{t("linkedProducts.title")}</Text>,
    store_linked_seller: (
      <Text strong>{t("contacts.groupsTable.linkedStore")}</Text>
    ),
  };

  const getPermissionLabel = (code: string) =>
    LABELS_MAP[code] ?? `Permission: ${code}`;

  const getConfigLabel = (code: string) =>
    LABELS_MAP[code] ?? `Config: ${code}`;

  /**
   * El vínculo con el store. Viene como array (`leftJoinAndMapMany` en `:sellerCode/stores`), pero
   * hay un solo vínculo por par (store, seller) — se acepta también la forma objeto porque el
   * listado la contempla y este `record` viaja por `location.state`.
   */
  const getLinkedStore = () =>
    Array.isArray(record?.storeLinkedSellers)
      ? record.storeLinkedSellers[0]
      : record?.storeLinkedSellers;

  const handleToggleStore = async (checked: boolean) => {
    setStructureLoading(true);
    try {
      const payload = {
        active: checked ? 1 : 0,
      };

      await axiosInstance.patch(
        `${API_URL_STORE_LINKED_SELLER}/${getLinkedStore()?.storeSellerId}`,
        payload,
      );
      setIsStoreActive(checked);
      message.success(`Store ${checked ? "enabled" : "disabled"} successfully`);
    } catch (error: any) {
      console.error("Error updating store:", error);
      if (error.message === "Insufficient permissions") {
        message.error(t("insufficient.permissions"));
      } else {
        message.error("Failed to update store status");
      }

      setIsStoreActive((prev) => !checked);
    } finally {
      setStructureLoading(false);
    }
  };

  const handleDelete = async () => {
    setStructureLoading(true);
    try {
      /**
       * `storeLinkedSellers` es un **array**: el endpoint `:sellerCode/stores` lo arma con
       * `leftJoinAndMapMany`. Leyendo `.storeSellerId` sobre el array salía `undefined`, así que
       * esto pegaba a `DELETE /store-linked-seller/undefined` y el desvinculado fallaba **siempre**,
       * con un "Failed to update store status" que no decía nada. La función de acá al lado
       * (`handleToggleStore`) ya usaba la forma correcta.
       */
      const linked = getLinkedStore();
      if (!linked?.storeSellerId) {
        message.error("No se encontró el vínculo con el store");
        return;
      }

      await axiosInstance.delete(
        `${API_URL_STORE_LINKED_SELLER}/${linked.storeSellerId}`,
      );

      message.success("Unlinked successfully");
      handleBack();
    } catch (error: any) {
      if (error.message === "Insufficient permissions") {
        message.error(t("insufficient.permissions"));
      } else {
        message.error("Failed to update store status");
      }
    } finally {
      setStructureLoading(false);
    }
  };

  const warningDelete = () => {
    Modal.confirm({
      title: t("storeoperator.message.title.removeoperator"),
      content: t("storeoperator.message.content.removestore"),
      onOk: () => {
        handleDelete();
      },
      width: 490,
      okText: t("storeperator.message.label.unlinked"),
      okType: "danger",
      okButtonProps: {
        type: "primary",
      },
      icon: <ExclamationCircleOutlined />,
      cancelButtonProps: {
        icon: <ArrowLeftOutlined />,
      },
      cancelText: t("buttons.back"),
      onCancel() { },
    });
  };

  return (
    <Spin spinning={structureLoading}>
      <Flex vertical>
        <Flex align="center" justify="space-between">
          <Flex align="center">
            <ListButton
              onClick={handleBack}
              hideText
              icon={<ArrowLeftOutlined />}
            />
            <Title style={{ marginBottom: 0, marginLeft: "10px" }} level={3}>
              <span style={{ textTransform: "capitalize" }}>
                {record?.name}
              </span>
              {` Store rules for `}
              <span style={{ textTransform: "capitalize" }}>
                {storeIdentifier}
              </span>
            </Title>
          </Flex>

          <Flex align="center" gap={6}>
            <Text style={{ color: token.colorTextSecondary }}>FAQ</Text>
            <ReadOutlined style={{ color: token.colorTextSecondary }} />
          </Flex>
        </Flex>

        <Collapse defaultActiveKey={["limits"]} style={{ marginTop: 24 }}>
          <Panel
            className="permissions-collapse"
            key="limits"
            header={
              <Flex vertical>
                <Text strong>
                  {t("label.permissionsofseller.sellerlimits")}
                </Text>
                <Text style={{ fontSize: 12 }}>
                  {t("label.permissionsofseller.instruction.sellerlimits1")}{" "}
                  <strong>
                    {t("label.permissionsofseller.instruction.sellerlimits2")}
                  </strong>
                </Text>
              </Flex>
            }
          >
            <Flex vertical gap={24} style={{ marginLeft: "12%" }}>
              <Flex gap={16}>
                <Text>Seller status in Store: </Text>
                <Flex vertical gap={8}>
                  <Flex align="center" gap={8}>
                    <Text style={{ color: token.colorTextSecondary }}>
                      Disabled by store
                    </Text>
                    <Switch
                      checked={isStoreActive}
                      onChange={handleToggleStore}
                      disabled
                      className={isStoreActive ? "active-switch" : ""}
                    />
                    <Text style={{ color: token.colorTextSecondary }}>
                      {isStoreActive
                        ? t("filter.products.labelStatusActive")
                        : t("filter.products.labelStatusDesactive")}
                    </Text>
                  </Flex>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t(
                      "label.permissionsofseller.instruction.sellerstatus.part1",
                    )}
                    <br />
                    {t(
                      "label.permissionsofseller.instruction.sellerstatus.part2",
                    )}
                  </Text>
                </Flex>
              </Flex>

              {/* <Flex gap={16}>
                <Text>Limit Seller service area: </Text>
                <Flex vertical gap={8}>
                  <Flex align="center" gap={8}>
                    <TextArea />
                  </Flex>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Limit the Seller geographic service area for the Store{" "}
                    <br />
                    within the specified <Link>GeoJSON.</Link> Without limits
                    when empty.
                  </Text>
                </Flex>
              </Flex> */}
            </Flex>
          </Panel>
        </Collapse>

        <Flex vertical gap={24}>
          <Collapse defaultActiveKey={["permissions"]}>
            <Panel
              className="permissions-collapse"
              key="permissions"
              header={
                <Flex justify="space-between">
                  <Text strong>{t("menu.label.salesManagement")}</Text>

                  <Flex gap={16} style={{ padding: "0 16px" }}>
                    {storesPermissions?.map((store: any) => (
                      <Text
                        key={store.id}
                        style={{ width: 160, textAlign: "center" }}
                      >
                        <ShopOutlined /> {store?.name}
                      </Text>
                    ))}
                  </Flex>
                </Flex>
              }
            >
              {groupedPermissions.map((group, index) => {
                // Single-row group (e.g. Grupos de contactos): render the header
                // with icon + title + subtitle + value on the right, no body rows.
                if ((group as any).singleHeader) {
                  const code = group.permissionCodes[0] ?? group.configCodes[0];
                  const isConfig = group.configCodes.length > 0;
                  const item = isConfig ? getConfig(code) : getPermission(code);
                  // Un API anterior a xcons-docs#445 no manda `categorias` para el par.
                  if (!item && group.key === "categorias") return null;
                  return (
                    <Card
                      key={`${group.key}-${index}`}
                      className={`permissions-card-section ${group.key === "contacts_groups" ? "contacts-groups-card" : ""}`}
                    >
                      <Flex justify="space-between" align="center">
                        <Flex vertical>
                          <Text strong style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            {group.icon} {group.title}
                          </Text>
                          <Text style={{ fontSize: 12, marginLeft: 19 }}>
                            {group.subtitle}
                          </Text>
                        </Flex>
                        <Flex style={{ textTransform: "capitalize" }}>
                          {renderPermissionValue(item, isConfig)}
                        </Flex>
                      </Flex>
                    </Card>
                  );
                }
                return (
                <>
                  <Card
                    key={`${group.key}-${index}`}
                    className={`permissions-card-section ${group.key === "contacts_groups"
                      ? "contacts-groups-card"
                      : ""
                      }`}
                    title={
                      !group.noButton &&
                      (group.permissionCodes.length > 1 ||
                        group.configCodes.length > 1) && (
                        <Flex justify="space-between" align="center">
                          <Flex vertical>
                            <Text
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              {group.icon} {group.title}
                            </Text>
                            <Text style={{ fontSize: "12px", marginLeft: 19 }}>
                              {group.subtitle}
                            </Text>
                          </Flex>

                          {group.configCodes.length > 0 ? (
                            <Flex>
                              <Text className="text-permission">
                                {renderPermissionValue(
                                  getConfig(group.configCodes[0]),
                                  true,
                                )}
                              </Text>
                            </Flex>
                          ) : group.mainPermissionCode ? (
                            <Flex>
                              <Text className="text-permission">
                                {renderPermissionValue(
                                  getPermission(group.mainPermissionCode),
                                )}
                              </Text>
                            </Flex>
                          ) : (
                            <Flex>
                              <Text className="text-permission">-</Text>
                            </Flex>
                          )}
                        </Flex>
                      )
                    }
                  >
                    {/* Renderizar configs */}
                    {group.configCodes.map((code, index) => (
                      <Flex
                        key={`${group.key}-${code}-${index}`}
                        justify="space-between"
                        align="center"
                        style={{
                          marginBottom: 18,
                        }}
                      >
                        <Text>{getConfigLabel(code)}</Text>

                        <Flex>
                          {code === "quote_max_manual_discount" ? (
                            <>
                              <Text className="text-permission">
                                {getConfig(code)?.value}
                              </Text>
                            </>
                          ) : code === "quote_access_margins" ? (
                            // 028 — la unidad del margen, no un toggle. Texto y no
                            // control deshabilitado, igual que la comisión de abajo.
                            <Text className="text-permission">
                              {marginUnitLabel(getConfig(code)?.value)}
                            </Text>
                          ) : code === "quote_commission_discount_level" ? (
                            <Text className="text-permission">
                              {getConfig(code)?.value === "editor"
                                ? t("label.commission.editor")
                                : getConfig(code)?.value === "viewer"
                                  ? t("label.commission.viewer")
                                  : t("label.commission.none")}
                            </Text>
                          ) : (
                            <>{renderPermissionValue(getConfig(code), true)}</>
                          )}
                        </Flex>
                      </Flex>
                    ))}

                    {/* Renderizar permisos */}
                    {group.permissionCodes.map((code, index) => (
                      <Flex
                        key={`${group.key}-${code}-${index}`}
                        justify="space-between"
                        align="center"
                        style={group.noButton ? {} : { marginBottom: 16 }}
                      >
                        <Text>{getPermissionLabel(code)}</Text>

                        <Flex>
                          {renderPermissionValue(getPermission(code))}
                        </Flex>
                      </Flex>
                    ))}
                  </Card>
                </>
                );
              })}
            </Panel>
          </Collapse>
        </Flex>

        <Flex justify="space-between" style={{ marginTop: 24 }}>
          <Button
            danger
            icon={<ApiOutlined />}
            iconPosition="end"
            onClick={warningDelete}
          >
            {t("button.unlink")}
          </Button>
        </Flex>
      </Flex>
    </Spin>
  );
};
